import {
  HTML_FRAME_CONTEXT_MENU_MESSAGE,
  framePx,
  isHtmlFrameMessage,
} from "@/components/shapes/html-frame/html-frame-bridge";

const selectedTextByIframe = new WeakMap<HTMLIFrameElement, string>();

/** Text the frame document had selected at its latest forwarded right click. */
export function iframeContextMenuSelection(iframe: HTMLIFrameElement): string {
  return selectedTextByIframe.get(iframe) ?? "";
}

/**
 * Forward right clicks reported by sandboxed HTML frame documents through the
 * frame's ordinary DOM handlers, as a contextmenu event on the iframe element.
 */
export function bridgeIframeContextMenus(root: HTMLElement): () => void {
  function handleMessage(event: MessageEvent) {
    if (event.data?.type !== HTML_FRAME_CONTEXT_MENU_MESSAGE) return;
    const iframe = Array.from(root.querySelectorAll("iframe")).find((frame) =>
      isHtmlFrameMessage(event, frame),
    );
    if (!iframe) return;
    const x = framePx(event.data.x);
    const y = framePx(event.data.y);
    if (x === null || y === null) return;
    const { selectedText } = event.data;
    selectedTextByIframe.set(
      iframe,
      typeof selectedText === "string" ? selectedText : "",
    );
    const rect = iframe.getBoundingClientRect();
    const scaleX = iframe.offsetWidth ? rect.width / iframe.offsetWidth : 1;
    const scaleY = iframe.offsetHeight ? rect.height / iframe.offsetHeight : 1;
    iframe.dispatchEvent(
      new MouseEvent("contextmenu", {
        bubbles: true,
        cancelable: true,
        clientX: rect.left + (iframe.clientLeft + x) * scaleX,
        clientY: rect.top + (iframe.clientTop + y) * scaleY,
        button: 2,
      }),
    );
  }
  window.addEventListener("message", handleMessage);
  return () => window.removeEventListener("message", handleMessage);
}

/**
 * The text selected where a right click inside `root` landed: an HTML
 * frame's document selection, else the page selection when it lies inside
 * `root`.
 */
export function contextMenuSelection(
  root: HTMLElement | null,
  target: EventTarget | null,
): string {
  if (root && target instanceof HTMLIFrameElement && root.contains(target))
    return iframeContextMenuSelection(target);
  const selection = window.getSelection();
  if (
    !root ||
    !selection ||
    selection.isCollapsed ||
    !selection.anchorNode ||
    !selection.focusNode ||
    !root.contains(selection.anchorNode) ||
    !root.contains(selection.focusNode)
  )
    return "";
  return selection.toString();
}
